import { useEffect, useState, type FormEvent } from 'react';
import type { MemoryRecord, UserSettings } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { toast } from '../components/toast';
import { EmptyState, ErrorState, Progress, Skeleton } from '../components/ui';
import { api } from '../lib/api';
import { chat, useChat } from '../lib/ai';
import { fmt } from '../lib/format';
import { google, useGoogle } from '../lib/google';
import { prefsStore, usePrefs } from '../lib/prefs';
import { isBrazilianVoice, isMaleVoice, isNaturalVoice, listVoices, pickVoice, voice } from '../lib/voice';
import { useLive } from '../lib/useLive';

/* Partes reais das Configurações ligadas à Etapa 2: provedor, limites e memória. */

const CATEGORIES = ['geral', 'preferência', 'pessoal', 'trabalho', 'rotina', 'saúde', 'outro'];
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Algo deu errado.');

function useAiStatus() {
  const c = useChat();
  useEffect(() => { void chat.loadStatus(); }, []);
  return c;
}

export function AiProviderStatus() {
  const { status, statusError } = useAiStatus();
  const on = Boolean(status?.configured);
  return (
    <div className="integration">
      <div className="integration-icon"><Icon name="brain" /></div>
      <div className="row-main">
        <div className="row-title">Modelo de linguagem (texto)</div>
        <div className="row-sub">{on
          ? <>Anthropic · <span className="mono">{status!.model}</span>. A chave fica só no servidor (Render).</>
          : statusError || 'Sem chave no servidor. Cadastre ANTHROPIC_API_KEY na Render para ligar a conversa.'}</div>
      </div>
      <span className={`badge ${on ? 'badge-signal' : 'badge-off'}`}><span className={`dot ${on ? 'on' : 'off'}`} />{on ? 'Configurado' : status ? 'Não configurado' : '…'}</span>
    </div>
  );
}

export function WebSearchStatus() {
  const { status } = useAiStatus();
  const on = Boolean(status?.search.enabled);
  return (
    <div className="integration">
      <div className="integration-icon"><Icon name="search" /></div>
      <div className="row-main">
        <div className="row-title">Busca na web</div>
        <div className="row-sub">{on
          ? <>Feita pela própria Anthropic, com a mesma chave. Até {status!.search.dailyLimit} buscas por dia; cada resposta mostra as fontes com link. Cada busca tem custo próprio na sua conta Anthropic.</>
          : status?.configured ? 'Desligada no servidor (WEB_SEARCH_ENABLED ou SEARCH_DAILY_QUERY_LIMIT na Render).' : 'Depende do modelo de IA configurado.'}</div>
      </div>
      <span className={`badge ${on ? 'badge-signal' : 'badge-off'}`}><span className={`dot ${on ? 'on' : 'off'}`} />{on ? 'Ligada' : 'Desligada'}</span>
    </div>
  );
}

export function LimitsLive() {
  const { status, statusError } = useAiStatus();
  if (!status) return statusError ? <p className="field-error">{statusError}</p> : <Skeleton kind="line" />;
  const row = (label: string, used: number, limit: number) => (
    <div className="usage-row">
      <div className="top"><span>{label}</span><span className="mono">{fmt.num(used)} / {fmt.num(limit)} tokens</span></div>
      <Progress value={(used / limit) * 100} />
    </div>
  );
  return (
    <div className="usage">
      {row('Hoje', status.today.tokens, status.limits.daily)}
      {row('Este mês', status.month.tokens, status.limits.monthly)}
      {status.search.enabled && (
        <div className="usage-row">
          <div className="top"><span>Buscas na web hoje</span><span className="mono">{status.today.searches} / {status.search.dailyLimit} · {status.month.searches} no mês</span></div>
          <Progress value={(status.today.searches / Math.max(1, status.search.dailyLimit)) * 100} />
        </div>
      )}
      <p className="row-sub">
        Aviso ao chegar em {status.limits.alertPercent}% · {status.today.requests} mensagens hoje, {status.month.requests} no mês.
        Os limites são definidos no servidor (USAGE_DAILY_TOKEN_LIMIT e USAGE_MONTHLY_TOKEN_LIMIT na Render). O valor em reais depende do preço do modelo na sua conta Anthropic; acompanhe em console.anthropic.com.
      </p>
    </div>
  );
}

function MemoryRow({ m, onChange, onRemove }: { m: MemoryRecord; onChange: (m: MemoryRecord) => void; onRemove: (id: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.text);
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.patch<{ item: MemoryRecord }>(`/api/memories/${m.id}`, { text });
      onChange(r.item);
      setEditing(false);
      toast('Memória atualizada.', 'check');
    } catch (err) { toast(errMsg(err), 'alert'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Apagar esta memória?\n\n“${m.text}”`)) return;
    try { await api.del(`/api/memories/${m.id}`); onRemove(m.id); toast('Memória apagada.', 'check'); }
    catch (err) { toast(errMsg(err), 'alert'); }
  };
  if (editing) {
    return (
      <form className="memory-item inline-form" onSubmit={save}>
        <label className="sr-only" htmlFor={`mem-${m.id}`}>Texto da memória</label>
        <input id={`mem-${m.id}`} className="input" value={text} maxLength={500} autoFocus onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !text.trim()}>Salvar</button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setText(m.text); setEditing(false); }}>Cancelar</button>
      </form>
    );
  }
  const pending = m.auto && !m.reviewedAt;
  const confirm = async () => {
    try { const r = await api.post<{ item: MemoryRecord }>(`/api/memories/${m.id}/review`); onChange(r.item); }
    catch (err) { toast(errMsg(err), 'alert'); }
  };
  const origin = m.auto ? 'aprendida sozinha pelo Jarvis' : m.source === 'assistant' ? 'sugerida pelo Jarvis, aceita por você' : 'escrita por você';
  return (
    <div className={`memory-item ${pending ? 'pending' : ''}`}>
      <div className="row-main">
        <div className="row-title">{m.text}</div>
        <div className="row-sub">{m.category} · {origin} · {fmt.short(m.createdAt)}{m.auto && m.reviewedAt ? ' · revisada' : ''}</div>
      </div>
      {pending && <button type="button" className="btn btn-sm" title="Está certo: manter" onClick={confirm}><Icon name="check" />Está certo</button>}
      <button type="button" className="btn btn-sm btn-ghost btn-icon" title="Editar" aria-label="Editar memória" onClick={() => setEditing(true)}><Icon name="edit" /></button>
      <button type="button" className="btn btn-sm btn-ghost btn-icon" title="Apagar" aria-label="Apagar memória" onClick={remove}><Icon name="trash" /></button>
    </div>
  );
}

export function MemoryManager() {
  const { state, set, reload } = useLive('/api/memories', (b: { items: MemoryRecord[] }) => b.items);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('geral');
  const [busy, setBusy] = useState(false);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post<{ item: MemoryRecord }>('/api/memories', { text, category });
      set((items) => [...items, r.item]);
      setText('');
      toast('Guardado na memória.', 'check');
    } catch (err) { toast(errMsg(err), 'alert'); } finally { setBusy(false); }
  };

  const items = state.status === 'ready' ? state.data : [];
  const pending = items.filter((m) => m.auto && !m.reviewedAt);
  const others = items.filter((m) => !(m.auto && !m.reviewedAt));
  const reviewAll = async () => {
    try {
      await api.post('/api/memories/review-all');
      const now = new Date().toISOString();
      set((list) => list.map((m) => (m.auto && !m.reviewedAt ? { ...m, reviewedAt: now } : m)));
      toast('Tudo confirmado.', 'check');
    } catch (err) { toast(errMsg(err), 'alert'); }
  };
  const row = (m: MemoryRecord) => (
    <MemoryRow key={m.id} m={m}
      onChange={(n) => set((list) => list.map((x) => (x.id === n.id ? n : x)))}
      onRemove={(id) => set((list) => list.filter((x) => x.id !== id))} />
  );

  return (
    <div>
      <AutoMemoryToggle />
      {pending.length > 0 && (
        <div className="mem-review">
          <div className="toolbar">
            <h3 className="subhead">Aprendido sozinho · para revisar ({pending.length})</h3>
            <button type="button" className="btn btn-sm" onClick={reviewAll}><Icon name="check" />Está tudo certo</button>
          </div>
          <p className="row-sub">Confirme o que estiver certo, corrija com o lápis ou apague. Até lá, o Jarvis já usa estes itens.</p>
          {pending.map(row)}
        </div>
      )}
      <h3 className="subhead">Memória</h3>
      <form className="inline-form mb-16" onSubmit={add}>
        <label className="sr-only" htmlFor="mem-new">Nova memória</label>
        <input id="mem-new" className="input" maxLength={500} placeholder="Ex.: Prefiro reuniões pela manhã" value={text} onChange={(e) => setText(e.target.value)} />
        <label className="sr-only" htmlFor="mem-cat">Categoria</label>
        <select id="mem-cat" className="input input-time" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !text.trim()}><Icon name="check" />Guardar</button>
      </form>
      {state.status === 'loading' && <Skeleton kind="line" />}
      {state.status === 'error' && <ErrorState compact error={state.error} onRetry={reload} />}
      {state.status === 'ready' && (others.length
        ? others.map(row)
        : !pending.length && <EmptyState compact icon="brain" title="Nenhuma memória guardada" text="Escreva acima algo que o Jarvis deve lembrar, ou converse com ele: o que você contar sobre você ele aprende sozinho." />)}
    </div>
  );
}

function AutoMemoryToggle() {
  const { state, set } = useLive('/api/settings', (b: { item: UserSettings }) => b.item);
  const [busy, setBusy] = useState(false);
  if (state.status !== 'ready') return null;
  const change = async (autoMemory: boolean) => {
    setBusy(true);
    try {
      const r = await api.patch<{ item: UserSettings }>('/api/settings', { autoMemory });
      set(() => r.item);
      toast(autoMemory ? 'O Jarvis volta a aprender sozinho.' : 'O Jarvis só vai sugerir; você decide cada item.', 'check');
    } catch (err) { toast(errMsg(err), 'alert'); } finally { setBusy(false); }
  };
  return (
    <label className="toggle-row mb-16">
      <input type="checkbox" checked={state.data.autoMemory} disabled={busy} onChange={(e) => change(e.target.checked)} />
      <span>
        <b>Aprender sozinho</b>
        <span className="row-sub">Quando você conta algo sobre você na conversa, o Jarvis guarda na hora e avisa, com opção de desfazer. Ele só aprende sozinho a partir das suas próprias palavras; ideias vindas de e-mails ou da web viram apenas sugestão. Desligado, ele só sugere e você decide cada item.</span>
      </span>
    </label>
  );
}

/* Voz do Jarvis: as vozes vêm do navegador e do sistema. */
export function VoicePicker() {
  const { voiceName } = usePrefs();
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>(() => listVoices());
  useEffect(() => {
    if (typeof speechSynthesis === 'undefined') return;
    const load = () => setVoices(listVoices());
    load();
    speechSynthesis.addEventListener('voiceschanged', load);
    return () => speechSynthesis.removeEventListener('voiceschanged', load);
  }, []);
  if (typeof speechSynthesis === 'undefined') return <p className="row-sub">Este navegador não fala em voz alta.</p>;
  const auto = pickVoice('');
  const current = pickVoice(voiceName);
  const males = voices.filter((v) => isMaleVoice(v) && isBrazilianVoice(v));
  const hasNaturalMale = males.some(isNaturalVoice);
  const label = (v: SpeechSynthesisVoice) => `${v.name.replace(/^Microsoft /, '').replace(/ - Portuguese.*$/, '')}${isMaleVoice(v) ? ' · masculina' : ''}${isNaturalVoice(v) ? ' · natural' : ''}`;
  const test = () => voice.speak('Às suas ordens. Estou acompanhando seus projetos, seus e-mails e o que acontece lá fora. Em que posso ajudar agora?');
  return (
    <div className="stack">
      <div className="form-grid">
        <div className="field">
          <label htmlFor="voice-pick">Voz do Jarvis</label>
          <select id="voice-pick" className="input" value={voiceName} onChange={(e) => prefsStore.set({ voiceName: e.target.value })}>
            <option value="">Automática{auto ? ` (${label(auto)})` : ''}</option>
            {voices.some(isBrazilianVoice) && <optgroup label="Português do Brasil">{voices.filter(isBrazilianVoice).map((v) => <option key={v.name} value={v.name}>{label(v)}</option>)}</optgroup>}
            {voices.some((v) => !isBrazilianVoice(v)) && <optgroup label="Português de Portugal">{voices.filter((v) => !isBrazilianVoice(v)).map((v) => <option key={v.name} value={v.name}>{label(v)}</option>)}</optgroup>}
          </select>
          <span className="hint">Automática escolhe sempre uma voz do Brasil. Salva neste navegador. Em uso agora: {current ? label(current) : 'nenhuma voz em português encontrada'}.</span>
        </div>
        <div className="field"><span className="label">Ouvir</span><button type="button" className="btn" onClick={test}><Icon name="play" />Ouvir a voz</button></div>
      </div>
      {!hasNaturalMale && (
        <div className="notice"><Icon name="info" /><span>
          {males.length ? 'Este navegador só tem uma voz masculina brasileira mais robótica.' : 'Este navegador não tem voz masculina em português do Brasil.'}
          {' '}Para a voz masculina natural (Antonio), abra o Jarvis no <strong>Microsoft Edge</strong>. No Chrome, as vozes naturais são só femininas.
        </span></div>
      )}
    </div>
  );
}

export function GmailIntegration() {
  const gs = useGoogle();
  const [busy, setBusy] = useState(false);
  useEffect(() => { void google.load(); }, []);
  const disconnect = async () => {
    if (!window.confirm('Desconectar o Gmail? A autorização é cancelada no Google e os e-mails importados são apagados do Jarvis.')) return;
    setBusy(true);
    try {
      const r = await google.disconnect();
      toast(`Gmail desconectado. ${r.emails} e-mails importados apagados${r.revoked ? '; autorização cancelada no Google.' : '. Confira em myaccount.google.com › Segurança se o acesso sumiu.'}`, 'check');
    } catch (e) { toast(errMsg(e), 'alert'); } finally { setBusy(false); }
  };
  const on = Boolean(gs?.connected && !gs.needsReconnect);
  let sub = 'Leitura de mensagens; rascunhos de resposta sob pedido. Nada é enviado.';
  if (gs && !gs.configured) sub = 'Credenciais do Google ainda não cadastradas na Render (Parte F do guia).';
  else if (gs?.needsReconnect) sub = `${gs.email}: o Google pediu uma nova autorização.`;
  else if (gs?.connected) sub = `${gs.email} · só leitura · conectado ${fmt.relative(gs.connectedAt!)}`;
  return (
    <div className="integration">
      <div className="integration-icon"><Icon name="mail" /></div>
      <div className="row-main"><div className="row-title">Gmail</div><div className="row-sub">{sub}</div></div>
      <span className={`badge ${on ? 'badge-signal' : gs?.needsReconnect ? 'badge-warn' : 'badge-off'}`}><span className={`dot ${on ? 'on' : 'off'}`} />{on ? 'Conectado' : gs?.needsReconnect ? 'Reconectar' : 'Não conectado'}</span>
      {gs?.configured && (gs.connected
        ? <>
            {gs.needsReconnect && <button type="button" className="btn btn-sm btn-primary" onClick={() => google.connect()}>Conectar de novo</button>}
            <button type="button" className="btn btn-sm btn-danger-ghost" disabled={busy} onClick={disconnect}>Desconectar</button>
          </>
        : <button type="button" className="btn btn-sm btn-primary" onClick={() => google.connect()}><Icon name="plug" />Conectar</button>)}
    </div>
  );
}